import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { STYLES_TYPE } from "../CapsuleButton";
import "./styles.scss";

const DEFAULT_ACTIVE_INDEX  = 0;

export type SegmentItem = {
    label?: any;
    value?: string | number;
    style?: STYLES_TYPE;
    selected?: boolean;
    enabled?: boolean;
    ref?: any;
    onClick?: (...args: any) => void;
};

type SegmentControlProps = {
    defaultSelected?: SegmentItem;
    segments: SegmentItem[];
    onSelect?: (value: any) => void;
    animated?: boolean;
    children?: any;
    className?: string;
    readOnly?: boolean;
    preventDefaultSelect?: boolean;
    allowSingle?: boolean;
    equalSegments?: boolean;
    fullWidth?: boolean;
};

export function SegmentedControl({
    segments,
    defaultSelected,
    onSelect,
    children,
    className = '',
    readOnly,
    preventDefaultSelect = false,
    allowSingle = false,
    equalSegments = false,
    fullWidth = false,
}: SegmentControlProps) {
    
    const slicerRef = useRef<any>();
    const segmentRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [isRendered, setIsRendered] = useState(false);

    const segmentsAmount = useMemo(
        () => segments?.length ?? 1,
        [segments.length],
    );

    readOnly = readOnly || (!allowSingle && segmentsAmount === 1);

    const defaultIndex = (() => {
        if (defaultSelected?.value) {
            const index = segments
                ?.map(({ value }) => value)
                .indexOf(defaultSelected?.value);
            if (index === -1) return DEFAULT_ACTIVE_INDEX;
            return index;
        }
        return DEFAULT_ACTIVE_INDEX;
    })();

    const [activeIndex, setActiveIndex] = useState<number>(defaultIndex);

    useEffect(() => setActiveIndex(defaultIndex), [defaultIndex])

    const moveSlicerTo = (index) => {
        const segment = segmentRefs.current[index];
        if (slicerRef?.current && segment) {
            slicerRef.current.style.width = `${segment.offsetWidth}px`;
            slicerRef.current.style.left = `${segment.offsetLeft}px`;
        }
    };

    useEffect(() => {
        const currentIndex = defaultIndex;
        setActiveIndex(currentIndex);
        moveSlicerTo(currentIndex);
        if(preventDefaultSelect) return;
        const segment = segments[currentIndex];
        segment.onClick && segment.onClick(segment.value);
        onSelect && onSelect(segment.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segmentsAmount, readOnly, defaultIndex]);

    useEffect(() => {
        setIsRendered(true);
    }, []);

    // Reposition the slicer when a segment's size changes (e.g. its label
    // grows once a date is picked), so the highlight keeps filling the segment.
    useEffect(() => {
        if (readOnly || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(() => moveSlicerTo(activeIndex));
        segmentRefs.current.forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex, segmentsAmount, readOnly]);

    return (
        <div className={` ${readOnly ? 'cna' : ''} ${className} position-relative`}>
            <div style={{ gridTemplateColumns: `repeat(${segmentsAmount}, ${equalSegments ? "1fr" : "auto"})`, ...(fullWidth ? { width: '100%' } : {}) }} className="controls c-bg-grey-240">
                {!readOnly && <div
                    className={`slicer ${isRendered ? "animated" : ""}`}
                    ref={slicerRef}
                ></div>}
                {segments.map(({ label: text, value, onClick }, i) => {
                    const id = 'segmented_control_' + value + "_" + i;
                    return (
                        <div
                            key={id}
                            ref={el => { segmentRefs.current[i] = el; }}
                            className={`segment ${
                                !readOnly && i === activeIndex
                                    ? "text-white"
                                    : "segment--inactive"
                            }`}
                            id={id}
                            onClick={() => {
                                if(readOnly) return;
                                if(activeIndex === i && !allowSingle) return;
                                moveSlicerTo(i);
                                setActiveIndex(i);
                                onClick && onClick(value);
                                onSelect && onSelect(value);
                            }}
                        >
                            {text}
                        </div>
                    );
                })}
            </div>
            {children}
        </div>
    );
}
